import {
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    Step,
    StepLabel,
    Stepper,
    Table,
    TableBody,
    TableCell,
    TableRow,
    TextField
} from "@mui/material";
import React, {forwardRef, useEffect, useImperativeHandle, useState} from "react";
import projectService from "../../services/ProjectService";
import {Assignment, CalendarMonth, Close, Factory, Upload} from "@mui/icons-material";
import projectTaskService from "../../services/ProjectTaskService";
import {useSnackbar} from "../../util/SnackbarProvider";
import VisuallyHiddenInput from "../common/VisuallyHiddenInput";
import userService from "../../services/UserService";
import {DateTimePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import vehicleService from "../../services/VehicleService";
import authService from "../../services/AuthService";
import axios from "axios";
import {API_URL} from "../../App";
import {toString} from "../../util/DateConverter";
import parse from "html-react-parser";
import calendarNoteService from "../../services/CalendarNoteService";
import taskService from "../../services/TaskService";

const ALL_STEPS = ['Cél', 'Leírás', 'Adatok', 'Fájlok'];
const DEFAULT_FORM_VALUES = {status: 'NOT_STARTED'}
const formValuesStorage = "task-assignment-dialog-form-values"
const targetOptionStyle = {fontSize: "calc(8vw - 0px)", margin: "0px"}
const targetOptions = [
    {icon: <Assignment sx={targetOptionStyle}/>, title: "\nFeladat", id: "task"},
    {icon: <CalendarMonth sx={targetOptionStyle}/>, title: "Naptár\nBejegyzés", id: "calendar-note"},
    {icon: <Factory sx={targetOptionStyle}/>, title: "Termelési\nFeladat", id: "production-task", disabled: true},
];

const TaskAssignmentDialog = forwardRef((_, ref) => {

    const [steps, setSteps] = useState(ALL_STEPS)
    const [open, setOpen] = useState(false)
    const [formValues, setFormValues] = useState(JSON.parse(localStorage.getItem(formValuesStorage)) || DEFAULT_FORM_VALUES)
    const [files, setFiles] = useState([])
    const [activeStep, setActiveStep] = useState(0)
    const [targetOption, setTargetOption] = useState()
    const {sendAlertMessage} = useSnackbar()
    const [waitingForServer, setWaitingForServer] = useState(false)
    const [onFinish, setOnFinish] = useState()

    useImperativeHandle(ref, () => ({
        openDialog: (values, f) => {
            setOpen(true);
            setFormValues(prev => ({...prev, ...values}));
            setOnFinish(() => f)
        },
    }));

    const [projects, setProjects] = useState(null);
    useEffect(() => {
        if (projects != null) return
        projectService.getSelectors()
            .then(response => setProjects(response))
            .catch(error => console.error(error))
    }, [projects]);

    const [projectTasks, setProjectTasks] = useState(null);
    useEffect(() => {
        if (projects == null || formValues?.project == null) return
        projectTaskService.search(formValues.project.id, {})
            .then(response => setProjectTasks(response.content))
            .catch(error => console.error(error))
    }, [formValues?.project]);

    const [users, setUsers] = useState(null);
    useEffect(() => {
        if (users != null) return
        userService.getSelectors()
            .then(response => setUsers(response))
            .catch(error => console.error(error))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [vehicles, setVehicles] = useState([]);
    useEffect(() => {
        vehicleService.getSelectors()
            .then(response => setVehicles(response))
            .catch(error => console.error(error))
    }, []);

    const onChange = (e, newValue, targetName) => {
        if (targetName === 'project')
            setFormValues((prev) => ({
                ...prev,
                projectTask: null
            }))
        const {name, value} = e?.target || {}
        setFormValues((prev) => ({
            ...prev,
            [name || targetName]: newValue || value
        }))
    }

    useEffect(() => {
        localStorage.setItem(formValues, JSON.stringify(formValuesStorage));
    }, [formValues]);

    const handleClose = () => {
        setOpen(false)
        setFiles([])
        setActiveStep(0)
    }

    const changeTargetOption = (id) => {
        setTargetOption(id)
        handleNext()
        if (id === 'calendar-note') setSteps(['Cél', 'Leírás', 'Adatok'])
        else if (id === 'task') setSteps(ALL_STEPS)
    }

    const handleSubmit = async () => {
        switch (targetOption) {
            case 'task':
                await createTask()
                break;
            case 'calendar-note':
                createCalendarNote()
                break;
            default:
                console.error('Invalid target option');
                return;
        }
    };

    const createTask = async () => {
        setWaitingForServer(true);
        const formData = new FormData();
        if (formValues != null) formData.append('request', new Blob([JSON.stringify(prepareRequestForTask())], {type: "application/json"}));
        files.forEach((file) => {
            formData.append('files', file);
        });
        const headers = {Authorization: `Bearer ${authService.getCurrentUser().accessToken}`};

        try {
            await axios.post(API_URL + `/tasks`, formData, {headers})
            sendAlertMessage('Feladat sikeresen létrehozva!');
            handleClose();
            setFormValues(DEFAULT_FORM_VALUES)
            setTargetOption(null)
        } catch (error) {
            sendAlertMessage(error?.response?.data?.message || 'Feladat létrehozása sikertelen', 'error');
            console.error(error);
        } finally {
            setWaitingForServer(false);
            if (onFinish) onFinish()
        }
    }

    const createCalendarNote = () => {
        setWaitingForServer(true);
        calendarNoteService.create(prepareRequestForCalendarNote())
            .then(() => {
                sendAlertMessage('Naptár bejegyzés sikeresen létrehozva!')
                handleClose()
                setFormValues(DEFAULT_FORM_VALUES)
                setTargetOption(null)
            })
            .catch(error => sendAlertMessage(error?.message || 'Naptár bejegyzés létrehozása sikertelen!', 'error'))
            .finally(setWaitingForServer(false))
    }

    const handleRemoveFile = (index) => {
        setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    };

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        setFiles((prevFiles) => [...prevFiles, ...selectedFiles]);
    };

    const handleNext = () => {
        if (activeStep === steps.length - 1) handleSubmit()
        else setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const isNextDisabled = () => {
        // if (activeStep === 1) {
        //     return formValues.project == null || formValues.projectTask == null;
        // } else if (activeStep === 1) {
        //     return formValues?.description == null || formValues?.description === "";
        // }
        return false;
    };

    const onUsersChange = (event, type) => {
        const {value} = event.target
        const newSelection = typeof value === 'string' ? value.split(',') : value;
        const uniqueSelection = newSelection.reduce((acc, user) => {
            const exists = acc.find(item => item.id === user.id);
            if (exists) return acc.filter(item => item.id !== user.id);
            else return [...acc, user];
        }, []);

        setFormValues((prev) => ({
            ...prev,
            [type]: uniqueSelection,
        }));
    };

    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: 48 * 4.5 + 8,
                width: 250
            },
        },
    };

    function getStyles(name, users) {
        if (users == null || users.map(user => user.name).indexOf(name) === -1) return
        return {
            backgroundColor: "#2e7d32",
            color: "white"
        };
    }

    const onDateTimeChange = (newValue, name) => {
        setFormValues((prev) => ({
            ...prev,
            [name]: newValue,
        }));
    };

    const prepareRequestForTask = () => {
        const copy = structuredClone(formValues)
        copy.deadline = toString(copy.deadline)
        copy.scheduleDateStart = toString(copy.scheduleDateStart)
        copy.scheduleDateEnd = toString(copy.scheduleDateEnd)
        copy.projectId = formValues.project?.id
        copy.taskName = formValues.projectTask?.description
        return copy
    }

    const prepareRequestForCalendarNote = () => {
        const copy = structuredClone(formValues)
        if (formValues.users) copy.userIds = formValues.users.map(user => user.id)
        delete copy.users
        return copy
    }

    const [similarTasks, setSimilarTasks] = useState([])
    useEffect(() => {
        if(formValues?.description?.length > 0) {
            taskService.search({description: formValues.description, size: 5})
                .then(response => setSimilarTasks(response.content))
                .catch(error => console.error(error))
        } else setSimilarTasks([]);
    }, [formValues?.description]);

    function renderTaskSteps() {
        return (
            <>
                {activeStep === 1 &&
                    <>
                        {createInputField("description", "Feladat Leírása", formValues?.description, onChange, false, false, false, "text", true, 20)}
                        {similarTasks?.length > 0 && 'Hasonló feladatok'}
                        {similarTasks.map((similarTask, index) =>(
                            <div onClick={() => window.open(window.location + "/" + similarTask.id)} key={index} style={{backgroundColor: "#ccc", cursor: "pointer", fontStyle: "italic", marginBottom: "2px", textWrap: "nowrap", textOverflow: "ellipsis", overflow: "clip"}}>
                                {similarTask.description}
                            </div>
                    ))}
                    </>}
                {activeStep === 2 &&
                    <Grid container columns={10} spacing={2}>
                        <Grid item xs={5}>
                            <Autocomplete
                                value={formValues?.project || null}
                                margin="normal"
                                label="Projekt Neve"
                                name="project"
                                autoHighlight
                                autoSelect
                                onChange={(event, newValue) => onChange(event, newValue, "project")}
                                options={projects || []}
                                isOptionEqualToValue={(item, other) => item.id === other.id}
                                getOptionLabel={(project) => project.name}
                                style={{marginTop: "16px"}}
                                renderInput={(params) => (
                                    <TextField {...params} label="Projekt Neve"/>
                                )}
                            />
                        </Grid>
                        <Grid item xs={5}>
                            <Autocomplete
                                value={formValues?.projectTask || null}
                                margin="normal"
                                label="Fejléc"
                                name="projectTask"
                                autoHighlight
                                freeSolo
                                disabled={formValues?.project?.id == null}
                                onChange={(event, newValue) => onChange(event, newValue, "projectTask")}
                                onInputChange={(event, newInputValue) => {
                                    onChange(event, {description: newInputValue}, "projectTask");
                                }}
                                options={projectTasks || []}
                                getOptionLabel={(projectTask) => projectTask.description}
                                isOptionEqualToValue={(item, other) => item.id === other.id}
                                style={{marginTop: "16px"}}
                                renderInput={(params) => (
                                    <TextField {...params} label="Fejléc"/>
                                )}
                            />
                        </Grid>
                        <Grid item xs={5}>
                            <FormControl sx={{
                                m: 1,
                                minWidth: 120,
                                marginTop: "0px",
                                marginLeft: "0px"
                            }} fullWidth>
                                <InputLabel id="candidates-label">Jelöltek</InputLabel>
                                <Select
                                    labelId="candidates-label"
                                    multiple
                                    value={formValues?.candidates || []}
                                    onChange={(e) => onUsersChange(e, "candidates")}
                                    label="Jelöltek"
                                    variant="outlined"
                                    input={<OutlinedInput id="select-multiple-chip"
                                                          label="Jelöltek"/>}
                                    renderValue={(selected) => (
                                        <Box sx={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: 1.0,
                                            margin: "-5px"
                                        }}>
                                            {selected.map((value) => (
                                                <Chip key={value.id} label={value.name}/>
                                            ))}
                                        </Box>
                                    )}
                                    MenuProps={MenuProps}
                                >
                                    {users.map((user) => (
                                        <MenuItem
                                            key={user.id}
                                            value={user}
                                            style={getStyles(user.name, formValues?.candidates)}
                                        >
                                            {user.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={5}>
                            <FormControl sx={{
                                m: 1,
                                minWidth: 120,
                                marginTop: "0px",
                                marginLeft: "0px"
                            }} fullWidth>
                                <InputLabel id="users-label">Dolgozók</InputLabel>
                                <Select
                                    labelId="users-label"
                                    multiple
                                    value={formValues?.users || []}
                                    onChange={(e) => onUsersChange(e, "users")}
                                    label="Dolgozók"
                                    variant="outlined"
                                    input={<OutlinedInput id="select-multiple-chip"
                                                          label="Dolgozók"/>}
                                    renderValue={(selected) => (
                                        <Box sx={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: 1.0,
                                            margin: "-5px"
                                        }}>
                                            {selected.map((value) => (
                                                <Chip key={value.id} label={value.name}/>
                                            ))}
                                        </Box>
                                    )}
                                    MenuProps={MenuProps}
                                >
                                    {users.map((user) => (
                                        <MenuItem
                                            key={user.id}
                                            value={user}
                                            style={getStyles(user.name, formValues?.users)}
                                        >
                                            {user.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={5}>
                            <Autocomplete
                                value={formValues?.vehicle || null}
                                margin="normal"
                                disableClearable
                                onChange={(event, newValue) => onDateTimeChange(newValue, "vehicle")}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                options={vehicles}
                                getOptionLabel={(vehicle) => vehicle.licensePlate}
                                style={{marginTop: "0px"}}
                                renderInput={(params) => (
                                    <TextField {...params} label="Jármű"/>
                                )}
                            />
                        </Grid>
                        <Grid item xs={5}>
                            <FormControl sx={{
                                minWidth: 120,
                                marginTop: "0px",
                                marginLeft: "0px"
                            }} fullWidth>
                                <InputLabel id="priority-label">Prioritás</InputLabel>
                                <Select
                                    variant="outlined"
                                    fullWidth
                                    labelId="priority-label"
                                    name="priority"
                                    label="Prioritás"
                                    value={formValues?.priority || "NORMAL"}
                                    defaultValue={"NORMAL"}
                                    onChange={(e, newValue) => onChange(e, newValue.value, "priority")}
                                    sx={{textAlign: "left"}}
                                >
                                    <MenuItem value={"EXTRA_IMPORTANT"}>Extra fontos</MenuItem>
                                    <MenuItem value={"IMPORTANT"}>Fontos</MenuItem>
                                    <MenuItem value={"NORMAL"}>Normál</MenuItem>
                                    <MenuItem value={"NOT_IMPORTANT"}>Nem fontos</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={5}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DateTimePicker label="Határidő"
                                                value={formValues?.deadline}
                                                sx={{width: "100%", marginTop: "0px"}}
                                                onChange={(newValue) => onDateTimeChange(newValue, "deadline")}/>
                            </LocalizationProvider>
                        </Grid>

                        <Grid item xs={5}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DateTimePicker label="Tervezett Kezdés"
                                                value={formValues?.scheduleDateStart}
                                                sx={{width: "100%", marginTop: "0px"}}
                                                onChange={(newValue) => onDateTimeChange(newValue, "scheduleDateStart")}/>
                            </LocalizationProvider>
                        </Grid>

                        <Grid item xs={5}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DateTimePicker label="Tervezett Befejezés"
                                                value={formValues?.scheduleDateEnd}
                                                sx={{width: "100%", marginTop: "8px"}}
                                                onChange={(newValue) => onDateTimeChange(newValue, "scheduleDateEnd")}/>
                            </LocalizationProvider>
                        </Grid>
                    </Grid>
                }

                {activeStep === 3 &&
                    <Grid container columns={10}>
                        <Grid item xs={8}>
                            <Table sx={{marginTop: "8px"}}>
                                <TableBody>
                                    {files.map((file, index) => (
                                        <React.Fragment key={index}>
                                            <TableRow>
                                                <TableCell sx={{textAlign: "left", padding: "0px 0px 0px 5px"}}>
                                                    {file.name}
                                                </TableCell>
                                                <TableCell sx={{textAlign: "left", padding: "0px"}}>
                                                    {(new Intl.NumberFormat().format((file.size / 1024).toFixed(2))).replace(',', '.')} KB
                                                </TableCell>
                                                <TableCell sx={{textAlign: "right", padding: "0px"}}>
                                                    <Button onClick={() => handleRemoveFile(index)} variant="text"
                                                            color='error'
                                                            sx={{marginLeft: "10px", padding: "5px", minWidth: "0px"}}>
                                                        <Close/>
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        </Grid>
                        <Grid item xs={files?.length > 0 ? 2 : 10} textAlign="center">
                            <Button variant='contained' component="label"
                                    sx={{
                                        minWidth: "0px",
                                        padding: "5px",
                                        margin: "10px 10px",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        borderRadius: "15px",
                                        backgroundColor: "#efbf04",
                                        ':hover': {backgroundColor: '#d6ab04'}
                                    }}>
                                <div><Upload sx={{fontSize: "calc(6vw - 0px)", margin: "0px"}}/></div>
                                <div style={{marginTop: "-10px", fontSize: "20px"}}>Fájlok</div>
                                <VisuallyHiddenInput type="file" multiple onChange={handleFileChange}/>
                            </Button>
                        </Grid>
                    </Grid>}
            </>)
    }

    function renderCalenderNoteSteps() {
        return (
            <>
                {activeStep === 1 && createInputField("noteText", "Jegyzet Szövege", formValues?.noteText, onChange, false, false, false, "text", true, 20)}
                {activeStep === 2 &&
                    <Grid container columns={10} spacing={2}>
                        <Grid item xs={5}>
                            <FormControl sx={{
                                m: 1,
                                minWidth: 120,
                                marginTop: "16px",
                                marginLeft: "0px"
                            }} fullWidth>
                                <InputLabel id="users-label">Dolgozók</InputLabel>
                                <Select
                                    labelId="users-label"
                                    multiple
                                    value={formValues?.users || []}
                                    onChange={(e) => onUsersChange(e, "users")}
                                    label="Dolgozók"
                                    variant="outlined"
                                    input={<OutlinedInput id="select-multiple-chip"
                                                          label="Dolgozók"/>}
                                    renderValue={(selected) => (
                                        <Box sx={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: 1.0,
                                            margin: "-5px"
                                        }}>
                                            {selected.map((value) => (
                                                <Chip key={value.id} label={value.name}/>
                                            ))}
                                        </Box>
                                    )}
                                    MenuProps={MenuProps}
                                >
                                    {users.map((user) => (
                                        <MenuItem
                                            key={user.id}
                                            value={user}
                                            style={getStyles(user.name, formValues?.users)}
                                        >
                                            {user.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={5}>
                            <FormControl sx={{minWidth: 120, marginTop: "16px", marginLeft: "0px"}} fullWidth>
                                <InputLabel id="repetition-type-label">Ismétlődés Típusa</InputLabel>
                                <Select
                                    variant="outlined"
                                    fullWidth
                                    name="repetitionType"
                                    label="Ismétlődés Típusa"
                                    labelId="repetition-type-label"
                                    sx={{textAlign: "left"}}
                                    value={formValues?.repetitionType || ""}
                                    onChange={(e, newValue) => onChange(e, newValue.value, "repetitionType")}
                                >
                                    <MenuItem value={""}>Nincs</MenuItem>
                                    <MenuItem value={"EVERY_WEEK"}>Hetente</MenuItem>
                                    <MenuItem value={"EVERY_MONTH"}>Havonta</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={5}>
                            <TextField
                                margin="dense"
                                required
                                name="targetDate"
                                label="Dátum"
                                fullWidth
                                value={formValues?.targetDate}
                                onChange={onChange}
                                type="date"
                                sx={{marginTop: "0px"}}
                                slotProps={{inputLabel: {shrink: true}}}
                            />
                        </Grid>

                        <Grid item xs={5}>
                            <TextField
                                margin="dense"
                                name="targetTime"
                                label="Időpont"
                                fullWidth
                                value={formValues?.targetTime}
                                onChange={onChange}
                                type="time"
                                sx={{marginTop: "0px"}}
                                slotProps={{inputLabel: {shrink: true}}}
                            />
                        </Grid>

                    </Grid>
                }
            </>
        )
    }

    return (
        <Dialog open={open} onClose={handleClose} PaperProps={{sx: {minWidth: "40vw"}}}>
            <DialogTitle sx={{background: "#1976d2", color: "white"}}>
                Feladat Kiosztása
            </DialogTitle>
            <DialogContent>
                <Stepper activeStep={activeStep} sx={{margin: "16px 0px 8px 0px", overflow: "hidden"}}>
                    {steps.map((label, index) => {
                        const stepProps = {};
                        const labelProps = {};
                        return (
                            <Step key={index} {...stepProps}>
                                <StepLabel {...labelProps}>{label}</StepLabel>
                            </Step>
                        );
                    })}
                </Stepper>
                {activeStep === 0 &&
                    <Grid container columns={8} spacing={2}>
                        {targetOptions.map((item, index) => (
                            <Grid item xs={2} key={index}>
                                <Button color={targetOption === item.id ? 'success' : 'primary'} variant='contained'
                                        disabled={item.disabled}
                                        sx={{
                                            minWidth: "0px",
                                            padding: "5px",
                                            margin: "10px 10px",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            borderRadius: "15px"
                                        }}
                                        onClick={() => {
                                            changeTargetOption(item.id)
                                        }}>
                                    <div>{item.icon}</div>
                                    <div style={{
                                        marginTop: "-10px",
                                        fontSize: "20px"
                                    }}>{parse(item.title.replace(/\n/g, '<br/>'))} </div>
                                </Button>
                            </Grid>))}
                    </Grid>
                }
                {targetOption === 'task' && renderTaskSteps()}
                {targetOption === 'calendar-note' && renderCalenderNoteSteps()}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="error" variant="outlined"
                        sx={{marginRight: activeStep === 0 ? "auto" : ""}}>
                    Mégse
                </Button>
                {activeStep > 0 &&
                    <Button onClick={handleBack} sx={{marginRight: "auto"}} color="primary" variant="contained">
                        Vissza
                    </Button>}
                {activeStep === 4 &&
                    <TextField
                        variant="outlined"
                        margin="normal"
                        fullWidth
                        label="Megjegyzés"
                        name="description"
                        autoComplete="off"
                        onChange={onChange}
                        value={formValues?.description || ""}
                    />}
                {waitingForServer ? <CircularProgress sx={{marginRight: "20px"}}/> : <>
                    <Button onClick={handleNext} color="primary" variant="contained" disabled={isNextDisabled()}>
                        {activeStep === steps.length - 1 ? 'Mentés' : 'Következő'}
                    </Button>
                </>}
            </DialogActions>
        </Dialog>
    )
})

function createInputField(name, label, value, onChange, required = false, autoFocus = false, disabled = false, type = "text", multiline = false, lg = 10) {
    return (<Grid item lg={lg} md={20} xs={40}>
        <TextField
            variant="outlined"
            margin="normal"
            required={required}
            fullWidth
            id={name}
            label={label}
            name={name}
            type={type}
            autoFocus={autoFocus}
            autoComplete="off"
            onChange={onChange}
            value={value}
            disabled={disabled}
            multiline={multiline}
            minRows={multiline ? 3 : 1}
        />
    </Grid>)
}

export default TaskAssignmentDialog;